import React, { useState } from 'react';
import axios from 'axios';
import colors, { shadows } from '../theme';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function FeedbackModal({ isOpen, onClose }) {
    const [category, setCategory] = useState('General feedback/suggestions');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        if (message.trim().length < 10) {
            setError('Please write at least 10 characters');
            setLoading(false);
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${API_URL}/feedback/submit`,
                { category, message },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setSuccess(true);
                setMessage('');
                setTimeout(() => {
                    setSuccess(false);
                    onClose();
                }, 2000);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to submit feedback');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Backdrop */}
            <div 
                onClick={onClose}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: colors.overlay.dark,
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                {/* Modal */}
                <div 
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        backgroundColor: colors.white,
                        borderRadius: '12px',
                        padding: '2rem',
                        width: '90%',
                        maxWidth: '500px',
                        boxShadow: shadows.xl,
                        position: 'relative'
                    }}
                >
                    {/* Close button */}
                    <button
                        onClick={onClose}
                        style={{
                            position: 'absolute',
                            top: '1rem',
                            right: '1rem',
                            background: 'none',
                            border: 'none',
                            fontSize: '1.5rem',
                            cursor: 'pointer',
                            color: colors.gray[500],
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '50%',
                            transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = colors.gray[100];
                            e.currentTarget.style.color = colors.gray[700];
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = colors.gray[500];
                        }}
                    >
                        ×
                    </button>

                    {/* Title */}
                    <h2 style={{ 
                        marginTop: 0, 
                        marginBottom: '1.5rem',
                        fontSize: '1.5rem',
                        fontWeight: '600',
                        color: colors.dark
                    }}>
                        📧 Contact Support
                    </h2>

                    {success ? (
                        <div style={{
                            padding: '2rem',
                            textAlign: 'center',
                            color: colors.success || '#10b981'
                        }}>
                            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✓</div>
                            <div style={{ fontSize: '1.1rem', fontWeight: '600' }}>
                                Feedback submitted successfully!
                            </div>
                            <div style={{ fontSize: '0.9rem', marginTop: '0.5rem', color: colors.gray[600] }}>
                                Thank you for your input.
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            {/* Category dropdown */}
                            <div style={{ marginBottom: '1.5rem' }}>
                                <label style={{ 
                                    display: 'block', 
                                    marginBottom: '0.5rem',
                                    fontWeight: '600',
                                    fontSize: '0.9rem',
                                    color: colors.dark
                                }}>
                                    Category *
                                </label>
                                <select
                                    value={category}
                                    onChange={(e) => setCategory(e.target.value)}
                                    required
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: `2px solid ${colors.gray[200]}`,
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        backgroundColor: colors.white,
                                        cursor: 'pointer',
                                        color: colors.dark
                                    }}
                                >
                                    <option value="General feedback/suggestions">General feedback/suggestions</option>
                                    <option value="Bug reports">Bug reports</option>
                                    <option value="Feature requests">Feature requests</option>
                                </select>
                            </div>

                            {/* Message textarea */}
                            <div style={{ marginBottom: '1.5rem' }}>
                                <label style={{ 
                                    display: 'block', 
                                    marginBottom: '0.5rem',
                                    fontWeight: '600',
                                    fontSize: '0.9rem',
                                    color: colors.dark
                                }}>
                                    Your Message *
                                </label>
                                <textarea
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    required
                                    minLength={10}
                                    placeholder="Tell us what's on your mind..."
                                    rows={6}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: `2px solid ${colors.gray[200]}`,
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        fontFamily: 'inherit',
                                        resize: 'vertical',
                                        color: colors.dark
                                    }}
                                />
                                <div style={{ 
                                    fontSize: '0.8rem', 
                                    color: colors.gray[500],
                                    marginTop: '0.25rem'
                                }}>
                                    Minimum 10 characters
                                </div>
                            </div>

                            {/* Error message */}
                            {error && (
                                <div style={{
                                    padding: '0.75rem',
                                    backgroundColor: colors.red[50],
                                    color: colors.red[600],
                                    borderRadius: '6px',
                                    marginBottom: '1rem',
                                    fontSize: '0.875rem'
                                }}>
                                    {error}
                                </div>
                            )}

                            {/* Submit button */}
                            <button
                                type="submit"
                                disabled={loading}
                                style={{
                                    width: '100%',
                                    padding: '0.875rem',
                                    backgroundColor: loading ? colors.gray[400] : colors.buttonBlue,
                                    color: colors.white,
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '1rem',
                                    fontWeight: '600',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.2s'
                                }}
                                onMouseOver={(e) => {
                                    if (!loading) e.currentTarget.style.opacity = '0.9';
                                }}
                                onMouseOut={(e) => {
                                    if (!loading) e.currentTarget.style.opacity = '1';
                                }}
                            >
                                {loading ? 'Sending...' : 'Submit Feedback'}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </>
    );
}

export default FeedbackModal;